import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Maximize2, Minimize2 } from 'lucide-react';
import type { Aula } from '@/types/masterclass';

interface SeriePlayerProps {
  aula: Aula | null;
  onTimeUpdate: (time: number) => void;
  onEnded: () => void;
  isTheater: boolean;
  onTheaterToggle: () => void;
}

export function SeriePlayer({
  aula,
  onTimeUpdate,
  onEnded,
  isTheater,
  onTheaterToggle,
}: SeriePlayerProps) {
  useLanguage();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [playerError, setPlayerError] = useState(false);

  // Atualizar timestamp a cada 10s (simulado)
  useEffect(() => {
    if (!aula) return;

    const interval = setInterval(() => {
      // Em produção, usar Vimeo Player API para obter tempo real
      // Por ora, apenas simulamos
      const mockTime = Math.floor(Math.random() * aula.duracaoMin * 60);
      onTimeUpdate(mockTime);
    }, 10000);

    return () => clearInterval(interval);
  }, [aula, onTimeUpdate]);

  if (!aula) {
    return (
      <div className="aspect-video bg-black rounded-xl flex items-center justify-center">
        <p className="text-white/60">{translateInterfaceText("Selecione uma aula para começar")}</p>
      </div>
    );
  }

  if (playerError) {
    return (
      <div className="aspect-video bg-black rounded-xl flex items-center justify-center">
        <div className="text-center">
          <p className="text-white mb-4">{translateInterfaceText("Falha ao carregar o vídeo")}</p>
          <Button onClick={() => setPlayerError(false)}>{translateInterfaceText("Tentar novamente")}</Button>
        </div>
      </div>
    );
  }

  const vimeoUrl = `https://player.vimeo.com/video/${aula.videoSource.videoId}?badge=0&autopause=0&player_id=0&app_id=58479`;

  return (
    <div className="relative">
      <div
        className="mc-player aspect-video bg-black rounded-xl overflow-hidden"
        style={{ border: '1px solid var(--gray-200)' }}
      >
        <iframe
          ref={iframeRef}
          src={vimeoUrl}
          className="w-full h-full"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title={aula.titulo}
          onError={() => setPlayerError(true)}
        />
      </div>

      {/* Botão Modo Teatro */}
      <Button
        variant="secondary"
        size="icon"
        className="absolute top-3 right-3 opacity-0 hover:opacity-100 transition-opacity"
        onClick={onTheaterToggle}
        aria-label={translateInterfaceText("Alternar modo teatro")}
      >
        {isTheater ? (
          <Minimize2 className="h-5 w-5" />
        ) : (
          <Maximize2 className="h-5 w-5" />
        )}
      </Button>
    </div>
  );
}
